import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { AxiosError } from 'axios';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useSWRMutation from 'swr/mutation';

import { SnackbarOpen } from '#app/Action/SnackbarAction';
import { dispatch } from '#app/App';
import AutoCompleteComponent from '#app/Component/AutoCompleteComponent';
import ButtonComponent from '#app/Component/ButtonComponent';
import TextFieldComponent from '#app/Component/TextFieldComponent';
import { Form, initFormHandler } from '#app/Handler/FormHandler';
import { generateApiPath, writeItem } from '#app/Handler/api';
import DepositByProductEntryPoints from '#app/entrypoints/depositByProduct';
import useAutocompleteByProduct from '#app/hooks/useAutocompleteByProduct';
import { DepositByProductPicking, DepositByProductPickingInput } from '#app/typings/depositByProduct';

type Props = {
  deposit: number;
  item?: DepositByProductPicking;
  onClose: () => void;
};

const EditModal: React.FC<Props> = ({ deposit, item, onClose }) => {
  const classes = useStyles();
  const [searchParams] = useSearchParams();
  const { byProducts, debouncedChange } = useAutocompleteByProduct({ deposit: deposit.toString() });
  const { trigger: update, isMutating: loading } = useSWRMutation<
    void,
    AxiosError,
    string,
    DepositByProductPickingInput
  >(
    item
      ? generateApiPath(DepositByProductEntryPoints.editPicking, { id: item.id.toString() })
      : generateApiPath(DepositByProductEntryPoints.addPicking, { id: deposit.toString() }),
    writeItem(item ? 'PUT' : 'POST'),
  );

  const [form, setForm] = useState<Form>({
    byProduct: {
      name: 'byProduct',
      label: 'Article',
      type: 'integer',
      options: { validation: ['required'] },
      defaultValue: item?.byProduct ?? searchParams.get('byProduct'),
      defaultValueText: item && `${item?.mark} ${item?.product} ${item?.packSize} ${item?.flavor}`,
    },
    pickingLocation: {
      name: 'pickingLocation',
      label: 'Emplacement de Picking',
      type: 'text',
      defaultValue: item?.pickingLocation,
    },
  });

  const handlerForm = initFormHandler(form, setForm);

  useEffect(() => {
    handlerForm.start();
  }, []);

  const handleSubmit = async (): Promise<void> => {
    const data = handlerForm.getData();

    try {
      await update({
        byProduct: data.byProduct,
        pickingLocation: data.pickingLocation,
      });
      dispatch(
        SnackbarOpen({
          text: 'Emplacement enregistré avec succès.',
          variant: 'success',
        }),
      );
      onClose();
    } catch {
      dispatch(
        SnackbarOpen({
          text: "Une erreur inattendue s'est produite.",
          variant: 'error',
        }),
      );
    }
  };

  return (
    <Dialog open maxWidth="xl" onClose={onClose}>
      <DialogTitle className={classes.title}>Attribuer un emplacement à un produit</DialogTitle>
      <DialogContent className={classes.content}>
        <AutoCompleteComponent
          id="byProduct"
          handler={handlerForm}
          loadOptions={(value, callback) => {
            debouncedChange(value);
            callback(byProducts);
          }}
        />
        <TextFieldComponent id="pickingLocation" handler={handlerForm} />
      </DialogContent>
      <DialogActions>
        <ButtonComponent color="#5E6E82" label="Annuler" onClick={onClose} disabled={loading} />
        <ButtonComponent label="Valider" loading={loading} onClick={handleSubmit} />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles({
  title: {
    fontSize: 15,
  },
  content: {
    minWidth: 300,
    width: '90vw',
    maxWidth: 600,
    minHeight: 200,
  },
});

export default EditModal;
